/**
 * @file Drinks.js
 * @author Stanley Chen
 * @description Drinks menu component that displays different categories of beverages
 * offered at Kiku Japanese Steakhouse with tabs for easy navigation
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import '../Menu/Menu.css';

const Drinks = () => {

    const location = useLocation();

    // 1. Initialize state immediately from location state, or default to 'hibachi'
    const [activeCategory, setActiveCategory] = useState(
        location.state?.activeCategory || 'cocktails'
    );

    useEffect(() => {
        // 2. Only update if the user navigates here from another link 
        // WHILE already on the page
        if (location.state && location.state.activeCategory) {
            setActiveCategory(location.state.activeCategory);
        }
    }, [location.key, location.state]);


    const menuCategories = [
        { id: 'sake', name: 'SAKE/SOJU' },
        { id: 'beer', name: 'BEER' },
        { id: 'wine', name: 'WINE' },
        { id: 'cocktails', name: 'COCKTAILS' },
        { id: 'spirits', name: 'SPIRITS' },
        { id: 'beverages', name: 'BEVERAGES' },
        { id: 'sangrita', name: 'SANGRITA' },
        { id: 'martini', name: "MARTINI" }
    ];


    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.5 }
        },
        exit: {
            opacity: 0,
            transition: { duration: 0.2 }
        }
    };

    // Simple MenuItem component
    const MenuItem = ({ name, price, description = "" }) => {
        return (
            <div className="menu-item">
                <div className="item-header">
                    <h3 className="item-name">{name}</h3>
                    <span className="item-price">{price}</span>
                </div>
                {description && <p className="item-description">{description}</p>}
            </div>
        );
    };

    // Section Header
    const SectionHeader = ({ title, subtitle = "" }) => (
        <div className="section-header">
            <h2>{title}</h2>
            {subtitle && <p className="section-subtitle">{subtitle}</p>}
        </div>
    );

    const renderMenuContent = () => {
        switch (activeCategory) {
            case 'sake':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="sake"
                    >
                        <div className="menu-section">
                            <SectionHeader title="Jinro Soju" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Grapefruit"
                                    price="$16.00"
                                    description="375 ml"
                                />
                                <MenuItem
                                    name="Greengrape"
                                    price="$16.00"
                                    description="375 ml"
                                />
                                <MenuItem
                                    name="Plum"
                                    price="$16.00"
                                    description="375 ml"
                                />
                                <MenuItem
                                    name="Strawberry"
                                    price="$16.00"
                                    description="375 ml"
                                />
                                <MenuItem
                                    name="Peach"
                                    price="$16.00"
                                    description="375 ml"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="House Sake" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Warm Sake"
                                    price="$6.00 (Small) / $9.00 (Large)"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Filtered Sake" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Tyku Silver"
                                    price="$27.00"
                                    description="330ml"
                                />
                                <MenuItem
                                    name="Joto Sake Yuzu"
                                    price="$30.00"
                                    description="500ml"
                                />
                                <MenuItem
                                    name="Moonstone Asian Pear"
                                    price="$16.00"
                                    description="300ml"
                                />
                                <MenuItem
                                    name="Moonstone Coconut Lemongrass"
                                    price="$16.00"
                                    description="300ml"
                                />
                                <MenuItem
                                    name="Moonstone Plum"
                                    price="$16.00"
                                    description="300ml"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Unfiltered Sake" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Gekkeikan Nigori"
                                    price="$16.00"
                                />
                                <MenuItem
                                    name="Tozai Sake Snow Maiden Nigori"
                                    price="$27.00"
                                    description="300ml"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Plum Sake" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Fuki Plum Wine"
                                    price="$8.00 (Glass) / $30.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Fuki Sake"
                                    price="$8.00 (Glass) / $30.00 (Bottle)"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Sparkling Sake" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Hou Hou Shu Rose"
                                    price="$25.00"
                                />
                            </div>
                        </div>

                    </motion.div>
                );

            case 'beer':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="beer"
                    >
                        <div className="menu-section">
                            <SectionHeader title="Beer" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="Asahi"
                                    price="$5.00"
                                    description="12 oz"
                                />
                                <MenuItem
                                    name="Sapporo"
                                    price="$5.00"
                                    description="12 oz"
                                />
                                <MenuItem
                                    name="Sapporo"
                                    price="$9.00"
                                    description="22 oz"
                                />
                                <MenuItem
                                    name="Kirin Ichiban"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Kirin Light"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Budweiser"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Bud Light"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Michelob Ultra"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Coors Light"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Heineken"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Corona"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Sam Adams"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Miller Lite"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Dog Fish 60mins PA"
                                    price="$5.00"
                                />
                                <MenuItem
                                    name="Yueng Ling"
                                    price="$5.00"
                                />
                            </div>
                        </div>
                    </motion.div>
                );

            case 'wine':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="wine"
                    >

                        <div className="menu-section">
                            <SectionHeader title="White Wine" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Kenwood Yulupa Chardonnay"
                                    price="$8.00 (Glass) / $25.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Kendall Jackson VR Chardonnay"
                                    price="$9.00 (Glass) / $30.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Robert Mondavi Private Selection Chardonnay"
                                    price="$8.00 (Glass) / $25.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Cavit Pinot Grigio"
                                    price="$8.00 (Glass) / $23.00 (Bottle)"
                                />
                                <MenuItem
                                    name="San Rocco Pinot Grigio"
                                    price="$7.00 (Glass) / $23.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Jacobs Creek Moscato"
                                    price="$8.00 (Glass) / $25.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Chateau Ste. Michelle Riesling"
                                    price="$8.00 (Glass) / $23.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Two Oceans Sauvignon Blanc"
                                    price="$8.00 (Glass) / $23.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Kim Crawford Sauvignon Blanc"
                                    price="$9.00 (Glass) / $30.00 (Bottle)"
                                />
                                <MenuItem
                                    name="CK Mondavi White Zinfandel"
                                    price="$9.00 (Glass) / $25.00 (Bottle)"
                                    description="750ml"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Red Wine" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Seven Moons Red Blend"
                                    price="$8.00 (Glass) / $25.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Velvet Devil Merlot"
                                    price="$7.00 (Glass) / $23.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Silver Palm Merlot"
                                    price="$35.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Jacobs Creek Shiraz"
                                    price="$7.00 (Glass) / $23.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Kenwood Cabernet Sauvignon"
                                    price="$7.00 (Glass) / $23.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Mark West Pinot Noir"
                                    price="$8.00 (Glass) / $27.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Kendall Jackson Merlot"
                                    price="$8.00 (Glass) / $30.00 (Bottle)"
                                />
                                <MenuItem
                                    name="Trivento Malbec"
                                    price="$8.00 (Glass) / $30.00 (Bottle)"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Sparkling Wine" />
                            <div className="menu-items">
                                <MenuItem
                                    name="J. Roget Brut"
                                    price="$8.00 (Glass)"
                                />
                                <MenuItem
                                    name="Lunetta Prosecco"
                                    price="$8.00 (Glass)"
                                />
                                <MenuItem
                                    name="Cupcake Moscato D'Asti"
                                    price="$10.00 (Glass)"
                                />
                                <MenuItem
                                    name="Gemma Di Luna Moscato"
                                    price="$32.00 (Bottle)"
                                    description="750ml"
                                />
                            </div>
                        </div>


                    </motion.div>
                );


            case 'sangrita':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="sangrita"
                    >

                        <div className="menu-section">
                            <SectionHeader title="Sangrita" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Apple"
                                    price="$10.00"
                                    description="Apple Schnapps, Cabernet Sauvignon, Triple Sec & Pineapple Juice"
                                />
                                <MenuItem
                                    name="Melon"
                                    price="$10.00"
                                    description="Melon Schnapps, Chardonnay, Triple Sec & Pineapple Juice"
                                />
                                <MenuItem
                                    name="Peach"
                                    price="$10.00"
                                    description="Peach Schnapps, Chardonnay, Triple Sec & Orange Juice"
                                />
                                <MenuItem
                                    name="Wild Cherry"
                                    price="$10.00"
                                    description="Cherry Brandy, Cabernet Sauvignon, Triple Sec, Orange Juice & Cranberry Juice"
                                />
                            </div>
                        </div>


                    </motion.div>
                );

            case 'beverages':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="beverages"
                    >
                        <div className="menu-section">
                            <SectionHeader title="Beverages" />
                            <div className="menu-items">
                                {/* Soft Drinks */}
                                <MenuItem
                                    name="Soft Drinks"
                                    description="Coke, Diet Coke, Sprite, Ginger Ale, Iced Tea, Lemonade"
                                    price="$3"
                                />

                                {/* Marble Sodas */}
                                <MenuItem
                                    name="Marble Sodas"
                                    description="Peach, Crange, Melon, Strawberry, Sprite"
                                    price="$4"
                                />

                                {/* Strawberry Lemonade */}
                                <MenuItem
                                    name="Strawberry Lemonade"
                                    description=""
                                    price="$4"
                                />

                                {/* Shirley Temple */}
                                <MenuItem
                                    name="Shirley Temple"
                                    description=""
                                    price="$4"
                                />

                                {/* Juices */}
                                <MenuItem
                                    name="Juice"
                                    description="Apple, Cranberry, Crange, Pineapple"
                                    price="$4"
                                />
                            </div>
                        </div>


                    </motion.div>
                );
            case 'martini':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="martini"
                    >

                        <div className="menu-section">
                            <SectionHeader title="Martinis" />
                            <div className="menu-items">
                                <MenuItem
                                    name="Sake Cosmo"
                                    description="Sake, Triple Sec, Lime, and Cranberry Juice"
                                    price="$10"
                                />
                                <MenuItem
                                    name="Saketini"
                                    description="Absolut Vodka, Sake, Fuki Plum Wine, and Ginger Liqueur"
                                    price="$10"
                                />
                                <MenuItem
                                    name="Geisha's Kiss"
                                    description="Absolut Citron Vodka, Triple Sec, Pomegranate Vodka, Lime and Cranberry Juice"
                                    price="$10"
                                />
                                <MenuItem
                                    name="Lychee Martini"
                                    description="Absolut Vodka, Lychee Liqueur, Triple Sec and Vermouth"
                                    price="$10"
                                />
                                <MenuItem
                                    name="Cucumber Martini"
                                    description="Hendricks Gin, Triple Sec, Vermouth, Fresh Cucumber"
                                    price="$10"
                                />
                                <MenuItem
                                    name="Sushitini"
                                    description="Absolut Vodka, Sake with a Wasabi Stuffed Olive Wrapped in Pickled Ginger"
                                    price="$10"
                                />
                                <MenuItem
                                    name="Espresso Martini"
                                    description="Vanilla Vodka, Kahlua, Espresso Liqueur, Bailey's"
                                    price="$10"
                                />
                            </div>
                        </div>

                    </motion.div>
                );

            case 'cocktails':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="cocktails"
                    >

                        <div className="menu-section">
                            <SectionHeader title="Cocktails" />
                            <div className="menu-items">

                                <MenuItem
                                    name="Rumphaker"
                                    description="Bacardi Gold Rum, Malibu Coconut Rum, Amaretto, Banana Liqueur, Grenadine & Polynesian Juice"
                                    price="$10"
                                />

                                <MenuItem
                                    name="Blue Hawaii"
                                    description="Malibu Coconut Rum, Blue Curacao & Pineapple Juice"
                                    price="$10"
                                />

                                <MenuItem
                                    name="Sake Margarita"
                                    description="Twist on the original using Sake in place of Tequila, with Peach, Apple, or Pomegranate"
                                    price="$10"
                                />

                                <MenuItem
                                    name="Mai Tai"
                                    description="Cruzan Dark Rum, Cruzan Light Rum, Bacardi Gold Rum, Orange Juice, Orange Curacao, Amaretto & Polynesian Juice"
                                    price="$10"
                                />

                                <MenuItem
                                    name="Singapore Sling"
                                    description="Beefeater Gin, Triple Sec, Cherry Brandy, Grenadine, Pineapple Juice & Lemon"
                                    price="$10"
                                />

                                <MenuItem
                                    name="Tropical Dream"
                                    description="Cruzan Mango Rum, Malibu Coconut Rum, Sky Vanilla Vodka, Banana Liqueur & Polynesian Juice"
                                    price="$10"
                                />


                                <MenuItem
                                    name="Zombie"
                                    description="Cruzan Dark Rum, Cruzan Light Rum, Captain Morgan Spiced Rum, Orange Curacao, Cherry Brandy, Pineapple Juice & Lemon"
                                    price="$10"
                                />

                                <MenuItem
                                    name="Banana Mama"
                                    description="Bacardi Light Rum, Banana Liqueur & Polynesian Juice"
                                    price="$10"
                                />

                                <MenuItem
                                    name="Mojito"
                                    description="Bacardi Light Rum, Simple Syrup, Mint, Lime Juice, with Apple, Peach, Mango, Ginger, or Strawberry"
                                    price="$10"
                                />

                                <MenuItem
                                    name="Margarita"
                                    description="Classic Lime, or Apple, Peach, Raspberry, or Mango"
                                    price="$10"
                                />
                            </div>
                        </div>


                    </motion.div>
                );

            case 'spirits':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="spirits"
                    >

                        <div className="menu-section">
                            <SectionHeader title="Vodka" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="House"
                                    price="$7"
                                />
                                <MenuItem
                                    name="Absolut"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Absolut Citrus"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Absolut Mandarin"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Chopin"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Grey Goose"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Pinnacle"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Pinnacle Citrus"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Pinnacle Whipped"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Skyy Mango"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Skyy Vanilla"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Stolichnaya"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Three Olives"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Three Olives Vanilla"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Three Olives Pomegranate"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Three Olives Raspberry"
                                    price="$8"
                                />
                                <MenuItem
                                    name="360 Double Chocolate"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Tito's Handmade"
                                    price="$9"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Rum" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="House"
                                    price="$7"
                                />
                                <MenuItem
                                    name="Appleton"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Bacardi"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Bacardi Gold"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Bacardi Limon"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Captain Morgan"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Cruzan Aged Dark"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Cruzan Aged 151"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Cruzan Light"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Cruzan Spiced"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Gosling's Black Seal"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Malibu Coconut"
                                    price="$8"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Cognac" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="Courvoisier VSOP"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Hennessy VSOP"
                                    price="$10"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Tequila" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="House"
                                    price="$7"
                                />
                                <MenuItem
                                    name="Jose Cuervo Gold"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Patron Silver"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Sauza Hornitos Plata"
                                    price="$9"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Gin" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="House"
                                    price="$7"
                                />
                                <MenuItem
                                    name="Beefeater"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Bombay"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Bombay Sapphire"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Hendrick's Gin"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Tanqueray"
                                    price="$9"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Brandy" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="Paul Masson Grande Amber"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Ginger Brandy"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Cherry Brandy"
                                    price="$8"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Irish Whiskey" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="Jameson"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Bushmills"
                                    price="$8"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Liqueurs" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="B&B"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Baileys Irish Cream"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Campari"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Chambord"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Di Saronno"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Drambuie"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Fireball"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Frangelico"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Godiva Chocolate"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Grand Marnier"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Jagermeister"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Kahlua"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Midori"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Pama"
                                    price="$8"
                                />
                                <MenuItem
                                    name="RumChata"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Southern Comfort"
                                    price="$8"
                                />
                                <MenuItem
                                    name="St. Germain"
                                    price="$8"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Canadian Whiskey" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="Lord Calvert"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Canadian Club"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Seagram's VO"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Crown Royal"
                                    price="$9"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Single Malt Scotch" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="Chivas 12 years"
                                    price="$12"
                                />
                                <MenuItem
                                    name="Chivas 18 years"
                                    price="$14"
                                />
                                <MenuItem
                                    name="Lephroaig 10 years"
                                    price="$10"
                                />
                                <MenuItem
                                    name="The Macallan 12 Year"
                                    price="$12"
                                />
                                <MenuItem
                                    name="The Glenlivet 18 Year"
                                    price="$14"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Blended Scotch Whiskey" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="House"
                                    price="$7"
                                />
                                <MenuItem
                                    name="Dewar's White Label"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Dewar's 18 Years"
                                    price="$14"
                                />
                                <MenuItem
                                    name="Johnnie Walker Red"
                                    price="$10"
                                />
                                <MenuItem
                                    name="Johnnie Walker Black"
                                    price="$12"
                                />
                                <MenuItem
                                    name="Johnnie Walker Blue"
                                    price="$72"
                                />
                                <MenuItem
                                    name="Toki Whiskey"
                                    price="$14"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Domestic Whiskey" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="House"
                                    price="$7"
                                />
                                <MenuItem
                                    name="Seagram's"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Jack Daniel's"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Jim Beam Honey"
                                    price="$8"
                                />
                                <MenuItem
                                    name="Wild Turkey"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Maker's Mark"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Knob Creek"
                                    price="$9"
                                />
                                <MenuItem
                                    name="Woodford Reserve"
                                    price="$9"
                                />
                            </div>
                        </div>


                    </motion.div>
                );


            default:
                return null;
        }
    };

    return (
        <div className="menu-page">
            <div className="menu-header">
                <h1>OUR DRINKS</h1>

                <div className="menu-tabs">
                    {menuCategories.map(category => (
                        <button
                            key={category.id}
                            className={`menu-tab ${activeCategory === category.id ? 'active' : ''}`}
                            onClick={() => setActiveCategory(category.id)}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>

            </div>

            <div className="menu-container">
                <AnimatePresence mode="wait">
                    {renderMenuContent()}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Drinks;